import React, {useEffect, useMemo, useState} from "react"
// MUI
import {
    Autocomplete,
    Box,
    Typography,
    Collapse,
    FormControl,
    InputLabel,
    Select, MenuItem, Button,
    TextField, IconButton,
} from "@mui/material";
import {ExpandLess, ExpandMore, FilterAltOff, Search, FilterAltOutlined, InfoOutlined} from "@mui/icons-material";
// Custom UI
import BrandRangeSelect from "@/components/UI/BrandRangeSelect/BrandRangeSelect";
import {packagingTokens as tk} from "@/theme/packagingTokens";
// Hooks
import {useUser} from "@/context/UserProvider";
import {useData} from "@/context/PackagingDataProvider";

// Fields committed on Search click (or Enter in a text input). Brand is
// intentionally excluded — it auto-fires so the Range dropdown can re-narrow
// via affectedRanges before the user picks a range. Organisation is no longer
// here — it's the app-wide sidebar filter (commonFilters.organisation).
const DEFERRED_TEXT_FIELDS = ["filterLabel", "filterDestination", "filterGtin"] as const
const DEFERRED_SELECT_FIELDS = ["filterRange", "filterType", "filterStatus", "filterMember"] as const

export default function ProductFilters(props: any) {

    const {
        gs1,
        modules,
        brands,
        affectedRanges,
        showFilters, setShowFilters,
        clearFilters,
        isFiltered,
        filters, applyFilters,
        t
    } = props

    // Draft state for every filter that waits for Search to commit. Brand is
    // excluded because it auto-fires (re-narrows Range via affectedRanges).
    const [draft, setDraft] = useState<Record<string, any>>({
        filterLabel: filters?.filterLabel ?? "",
        filterDestination: filters?.filterDestination ?? "",
        filterGtin: filters?.filterGtin ?? "",
        filterRange: filters?.filterRange ?? "",
        filterType: filters?.filterType ?? "",
        filterStatus: filters?.filterStatus ?? "",
        filterMember: filters?.filterMember ?? "",
    })

    // Re-sync draft when committed filters change from outside (initial
    // hydration from saved user settings, Clear Filters, brand auto-apply
    // which also resets filterRange to "" via ProductsTab's cascade).
    useEffect(() => {
        setDraft({
            filterLabel: filters?.filterLabel ?? "",
            filterDestination: filters?.filterDestination ?? "",
            filterGtin: filters?.filterGtin ?? "",
            filterRange: filters?.filterRange ?? "",
            filterType: filters?.filterType ?? "",
            filterStatus: filters?.filterStatus ?? "",
            filterMember: filters?.filterMember ?? "",
        })
    }, [
        filters?.filterLabel,
        filters?.filterDestination,
        filters?.filterGtin,
        filters?.filterRange,
        filters?.filterType,
        filters?.filterStatus,
        filters?.filterMember,
    ])

    // ── Creator filter options ──
    // The org tree (GET /orgs) is the only source that carries every user's
    // email, org owner included. Scoping mirrors the backend's products query:
    // a selected organisation lists its direct users only (GetUserIDsFromOrg
    // does not descend), "all organisations" flattens the whole tree.
    const {fullOrganisation, getFullOrganisation} = useUser()
    const {commonFilters} = useData()

    useEffect(() => {
        if (!fullOrganisation) getFullOrganisation()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const creatorOptions = useMemo(() => {
        const list: { user_id: string; email: string }[] = []
        const seen = new Set<string>()
        const collectUsers = (org: any, deep: boolean) => {
            if (!org) return
            ;(org.users ?? []).forEach((u: any) => {
                if (u?.user_id && u?.email && !seen.has(u.user_id)) {
                    seen.add(u.user_id)
                    list.push({user_id: u.user_id, email: u.email})
                }
            })
            if (deep) (org.sub_organisations ?? []).forEach((sub: any) => collectUsers(sub, true))
        }
        const findOrg = (org: any, id: string): any => {
            if (!org) return null
            if (org.organisation_id === id) return org
            for (const sub of org.sub_organisations ?? []) {
                const hit = findOrg(sub, id)
                if (hit) return hit
            }
            return null
        }
        const selectedOrgId = commonFilters?.organisation
        if (selectedOrgId) collectUsers(findOrg(fullOrganisation, selectedOrgId), false)
        else collectUsers(fullOrganisation, true)
        return list.sort((a, b) => a.email.localeCompare(b.email))
    }, [fullOrganisation, commonFilters?.organisation])

    const setDraftField = (key: string, value: any) => setDraft((d) => ({...d, [key]: value}))

    const isFieldDirty = (key: string): boolean => {
        if (key === "filterGtin") return String(draft.filterGtin ?? "").trim() !== (filters?.[key] ?? "")
        return draft[key] !== (filters?.[key] ?? "")
    }

    const allFields = ["filterLabel", "filterDestination", "filterGtin", "filterRange", "filterType", "filterStatus", "filterMember"]
    const isDirty = useMemo(() => allFields.some(isFieldDirty), [draft, filters])

    const commitDraft = () => {
        if (!isDirty) return

        const committable: Record<string, any> = {}
        for (const key of [...DEFERRED_TEXT_FIELDS, ...DEFERRED_SELECT_FIELDS] as string[]) {
            let v = draft[key]
            if (key === "filterGtin") v = String(v ?? "").trim()
            if (v !== (filters?.[key] ?? "")) committable[key] = v
        }
        if (Object.keys(committable).length > 0) applyFilters(committable)
    }

    const onTextKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault()
            commitDraft()
        }
    }

    // ── Multi-GTIN chips ──
    // draft.filterGtin stays a comma-joined string (what the API's `gtin`
    // param consumes); the Autocomplete just renders it as chips. Enter or a
    // comma turns the pending text into a chip; Enter on an empty input
    // triggers the search like the other text fields.
    const gtinChips: string[] = String(draft.filterGtin ?? "").split(",").map((v) => v.trim()).filter(Boolean)
    const [gtinInput, setGtinInput] = useState<string>("")

    const setGtinChips = (chips: string[]) =>
        setDraftField("filterGtin", chips.map((v) => v.trim()).filter(Boolean).join(","))

    const addGtinInput = (raw: string) => {
        const parts = raw.split(",").map((v) => v.trim()).filter(Boolean)
        if (parts.length) setGtinChips([...gtinChips, ...parts.filter((p) => !gtinChips.includes(p))])
    }

    // Dirty-field visual: MUI "secondary" color is used everywhere else in
    // this tree for the "action needed" affordance (Clear Filters alert,
    // active column radios). Reusing keeps it consistent.
    const dirtyColor = (key: string): "primary" | "secondary" => (isFieldDirty(key) ? "secondary" : "primary")

    return (
        <Box
            sx={{
                backgroundColor: tk.color.surface,
                border: `1px solid ${tk.color.border}`,
                borderRadius: tk.radius.card,
                p: 2.5,
                mb: 2.5,
            }}
        >
            {/* Header */}
            <Box
                onClick={() => setShowFilters(!showFilters)}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    mb: showFilters ? 1.75 : 0,
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                    <FilterAltOutlined sx={{fontSize: 16, color: tk.color.textTertiary}} />
                    <Typography sx={{fontSize: "13.5px", fontWeight: 700}}>
                        {t("filters")}
                    </Typography>
                </Box>
                <IconButton size={"small"} sx={{color: tk.color.textTertiary}}>
                    {showFilters ? <ExpandLess fontSize={"small"} /> : <ExpandMore fontSize={"small"} />}
                </IconButton>
            </Box>

            <Collapse in={showFilters && !!filters}>
                {/* Filters grid */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
                        gap: 1.5,
                        alignItems: "end",
                    }}
                >
                    <BrandRangeSelect
                        small
                        label={t("brand")}
                        type={"brand"}
                        data={brands}
                        value={filters?.filterBrand}
                        setValue={(e: any) => {
                            // Brand is the only auto-fire filter. Commit with
                            // filterRange cleared so the previously-picked range
                            // (which belonged to a different brand) doesn't
                            // linger in the committed state.
                            const next = e.target.value
                            if (next === filters.filterBrand) return
                            applyFilters({filterBrand: next, filterRange: ""})
                        }}
                        {...{t}}
                    />
                    <BrandRangeSelect
                        small
                        label={t("range")}
                        type={"range"}
                        data={affectedRanges}
                        value={draft.filterRange}
                        setValue={(e: any) => setDraftField("filterRange", e.target.value)}
                        color={dirtyColor("filterRange")}
                        focused={isFieldDirty("filterRange") || undefined}
                        {...{t}}
                    />
                    <TextField
                        label={t("name_or_short_url")}
                        placeholder={t("name_or_short_url")}
                        fullWidth
                        size="small"
                        color={dirtyColor("filterLabel")}
                        focused={isFieldDirty("filterLabel") || undefined}
                        value={draft.filterLabel}
                        onChange={(e) => setDraftField("filterLabel", e.target.value)}
                        onKeyDown={onTextKeyDown}
                    />
                    <TextField
                        label={t("product_default_url")}
                        placeholder={t("product_default_url")}
                        fullWidth
                        size="small"
                        color={dirtyColor("filterDestination")}
                        focused={isFieldDirty("filterDestination") || undefined}
                        value={draft.filterDestination}
                        onChange={(e) => setDraftField("filterDestination", e.target.value)}
                        onKeyDown={onTextKeyDown}
                    />
                    {gs1 && (
                        <Autocomplete
                            multiple
                            freeSolo
                            size={"small"}
                            options={[]}
                            value={gtinChips}
                            inputValue={gtinInput}
                            onInputChange={(_, value) => {
                                // A comma commits the pending text as a chip.
                                if (value.includes(",")) {
                                    addGtinInput(value)
                                    setGtinInput("")
                                } else setGtinInput(value)
                            }}
                            onChange={(_, value) => setGtinChips(value as string[])}
                            onBlur={() => {
                                if (gtinInput.trim()) {
                                    addGtinInput(gtinInput)
                                    setGtinInput("")
                                }
                            }}
                            onKeyDown={(e) => {
                                // Empty input + Enter = run the search (chips
                                // are already committed to the draft).
                                if (e.key === "Enter" && !gtinInput.trim()) {
                                    e.preventDefault()
                                    commitDraft()
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    data-step-target={"filter-gtin"}
                                    label={t("filter_gtin_placeholder", "GTIN")}
                                    placeholder={gtinChips.length ? "" : t("filter_gtin_multiple_placeholder", "GTINs — Enter or comma to add")}
                                    color={dirtyColor("filterGtin")}
                                    focused={isFieldDirty("filterGtin") || undefined}
                                />
                            )}
                        />
                    )}
                    <FormControl
                        fullWidth
                        size={"small"}
                        color={dirtyColor("filterType")}
                        focused={isFieldDirty("filterType") || undefined}
                        sx={{display: (!!modules && modules.gs1 && modules.geoloc) ? "flex" : "none"}}
                    >
                        <InputLabel id={`type-selector`}>{t("type")}</InputLabel>
                        <Select
                            label={t("type")}
                            labelId={`type-selector`}
                            id={`packaging-type-selector`}
                            value={draft.filterType || ""}
                            onChange={(e: any) => setDraftField("filterType", e.target.value)}
                        >
                            <MenuItem value={""}>{t("no_filter")}</MenuItem>
                            <MenuItem value={"Standard"}>{t("geolocation")}</MenuItem>
                            <MenuItem value={"GS1"}>{t("gs1")}</MenuItem>
                        </Select>
                    </FormControl>
                    <Autocomplete
                        size={"small"}
                        autoHighlight
                        options={creatorOptions}
                        getOptionLabel={(option: any) => option?.email ?? ""}
                        isOptionEqualToValue={(option: any, value: any) => option?.user_id === value?.user_id}
                        value={creatorOptions.find((o) => o.user_id === draft.filterMember) ?? null}
                        onChange={(_, value: any) => setDraftField("filterMember", value?.user_id ?? "")}
                        noOptionsText={t("no_options")}
                        renderOption={(optionProps, option: any) => {
                            const {key, ...rest} = optionProps as any
                            return (
                                <Box component={"li"} key={key} {...rest}>
                                    <Typography variant={"body2"} noWrap>{option.email}</Typography>
                                </Box>
                            )
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("owner")}
                                placeholder={t("owner")}
                                color={dirtyColor("filterMember")}
                                focused={isFieldDirty("filterMember") || undefined}
                            />
                        )}
                    />
                    <FormControl
                        fullWidth
                        size={"small"}
                        color={dirtyColor("filterStatus")}
                        focused={isFieldDirty("filterStatus") || undefined}
                    >
                        <InputLabel id={`status-selector`}>{t("status")}</InputLabel>
                        <Select
                            label={t("status")}
                            labelId={`status-selector`}
                            id={`packaging-status-selector`}
                            value={draft.filterStatus || ""}
                            onChange={(e: any) => setDraftField("filterStatus", e.target.value)}
                        >
                            <MenuItem value={""}>{t("all")}</MenuItem>
                            <MenuItem value={"false"}>{t("active")}</MenuItem>
                            <MenuItem value={"true"}>{t("archived")}</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        fullWidth
                        variant="contained"
                        disableElevation
                        disabled={!isDirty}
                        startIcon={<Search />}
                        onClick={commitDraft}
                        sx={{height: 40, textTransform: "none", fontWeight: 600, borderRadius: tk.radius.sm}}
                    >
                        {t("filter_gtin_search", "Search")}
                    </Button>
                </Box>

                {/* Active filters bar */}
                <Collapse in={isFiltered}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mt: 1.75,
                            pt: 1.5,
                            borderTop: `1px solid ${tk.color.borderLight}`,
                        }}
                    >
                        <Box sx={{display: "flex", alignItems: "center", gap: 0.75, color: tk.color.greenDark}}>
                            <InfoOutlined sx={{fontSize: 15}} />
                            <Typography sx={{fontSize: "12.5px", fontWeight: 500}}>
                                {t("filters_are_active")}
                            </Typography>
                        </Box>
                        <Button
                            onClick={() => clearFilters()}
                            size={"small"}
                            startIcon={<FilterAltOff sx={{fontSize: 14}} />}
                            sx={{
                                textTransform: "none",
                                fontSize: "12px",
                                fontWeight: 600,
                                color: tk.color.textSecondary,
                                border: `1px solid ${tk.color.border}`,
                                borderRadius: tk.radius.sm,
                                px: 1.5,
                                "&:hover": {
                                    backgroundColor: tk.color.red100,
                                    borderColor: tk.color.red,
                                    color: tk.color.red,
                                },
                            }}
                        >
                            {t("clear_filters")}
                        </Button>
                    </Box>
                </Collapse>
            </Collapse>
        </Box>
    )
}
